import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, DocInput, Field, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { CertificateView, FiscalCertificateResponse } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { formatDocument } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import { PROVIDER_STATUS_LABEL, PROVIDER_STATUS_TONE, certificateBadge, dateBR, hexGroups } from "./labels";

/** Preferências › Fiscal › Certificado digital (T2.1, RF-PRF-08): CNPJ da empresa, certificado A1 ativo e histórico. */
export function CertificatePage() {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canUpload = can(perms, "fiscal_certificates", "update");
  const canCnpj = can(perms, "companies", "update");
  const key = ["fiscal-certificate", companyId];
  const q = useQuery({ queryKey: key, queryFn: () => api<FiscalCertificateResponse>("/fiscal/certificate") });
  const data = q.data;
  return (
    <>
      <ContentHeader
        title="Certificado digital"
        breadcrumb={["Preferências", "Fiscal", "Certificado digital"]}
      />
      <QueryStatus loading={q.isLoading} error={q.error} />
      {data && (
        <>
          <CompanyCnpjCard cnpj={data.company.cnpj} legalName={data.company.legalName} canEdit={canCnpj} />
          <CurrentCard current={data.current} provider={data.provider} />
          {canUpload && <UploadCard hasCurrent={!!data.current} hasCnpj={!!data.company.cnpj} />}
          <HistoryCard history={data.history} />
        </>
      )}
    </>
  );
}

function CompanyCnpjCard(p: { cnpj: string | null; legalName: string; canEdit: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [cnpj, setCnpj] = useState(p.cnpj ?? "");
  const [error, setError] = useState<string | undefined>();
  useEffect(() => {
    setCnpj(p.cnpj ?? "");
  }, [p.cnpj]);
  const save = useMutation({
    mutationFn: () =>
      api<{ cnpj: string }>("/fiscal/company", { method: "PUT", body: JSON.stringify({ cnpj }) }),
    onSuccess: () => {
      setError(undefined);
      toast("success", "CNPJ da empresa salvo");
      void qc.invalidateQueries({ queryKey: ["fiscal-certificate", companyId] });
    },
    onError: (e) => {
      setError(e instanceof ApiFailure ? (e.fields?.cnpj ?? e.message) : errorMessage(e));
      toast("danger", errorMessage(e));
    },
  });
  return (
    <section className="box card mb-3" aria-labelledby="cert-company">
      <div className="card-body">
        <h2 id="cert-company" className="h5">
          Empresa
        </h2>
        <p className="text-muted small">
          {p.legalName}. O certificado precisa ser do CNPJ da empresa ou de outro estabelecimento com a mesma
          raiz (8 primeiros dígitos), como exige a SEFAZ.
        </p>
        {!p.cnpj && (
          <div className="alert alert-warning py-2" role="alert">
            Informe o CNPJ da empresa para enviar o certificado digital.
          </div>
        )}
        <form
          className="row align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (p.canEdit) save.mutate();
          }}
        >
          <Field label="CNPJ da empresa" required className="col-md-6" error={error}>
            <DocInput personType="J" value={cnpj} onChange={setCnpj} disabled={!p.canEdit} maxLength={18} />
          </Field>
          {p.canEdit && (
            <div className="col-md-3 mb-3">
              <button type="submit" className="btn btn-outline-primary" disabled={save.isPending || !cnpj}>
                Salvar CNPJ
              </button>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function Details({ c }: { c: CertificateView }): ReactElement {
  return (
    <dl className="row mb-0">
      <dt className="col-sm-4">Titular</dt>
      <dd className="col-sm-8">{c.subjectName}</dd>
      <dt className="col-sm-4">CNPJ do certificado</dt>
      <dd className="col-sm-8">{formatDocument(c.subjectCnpj)}</dd>
      <dt className="col-sm-4">Emitido por</dt>
      <dd className="col-sm-8">{c.issuerName}</dd>
      <dt className="col-sm-4">Validade</dt>
      <dd className="col-sm-8">
        {dateBR(c.validFrom)} a {dateBR(c.validTo)}
      </dd>
      <dt className="col-sm-4">Número de série</dt>
      <dd className="col-sm-8 text-break">
        <code>{hexGroups(c.serialNumber)}</code>
      </dd>
      <dt className="col-sm-4">Impressão digital (SHA-1)</dt>
      <dd className="col-sm-8 text-break">
        <code>{hexGroups(c.thumbprintSha1)}</code>
      </dd>
      <dt className="col-sm-4">Enviado em</dt>
      <dd className="col-sm-8">{dateTime(c.createdAt)}</dd>
    </dl>
  );
}

function CurrentCard(p: {
  current: CertificateView | null;
  provider: FiscalCertificateResponse["provider"];
}): ReactElement {
  const c = p.current;
  const badge = c ? certificateBadge(c.validTo) : null;
  return (
    <section className="box card mb-3" aria-labelledby="cert-current">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <h2 id="cert-current" className="h5 mb-0 me-auto">
            Certificado A1 ativo
          </h2>
          {badge && <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>}
          {c && (
            <StatusBadge tone={PROVIDER_STATUS_TONE[c.providerStatus]}>
              {PROVIDER_STATUS_LABEL[c.providerStatus]}
            </StatusBadge>
          )}
        </div>
        {!c ? (
          <p className="text-muted mb-0">
            Nenhum certificado enviado. O certificado digital A1 (.pfx ou .p12) é necessário para emitir notas
            fiscais.
          </p>
        ) : (
          <>
            <Details c={c} />
            {c.providerStatus === "failed" && c.providerError && (
              <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
                {c.providerError}
              </div>
            )}
            {p.provider === "sandbox" && (
              <p className="text-muted small mt-3 mb-0">Ambiente de testes: provedor fiscal simulado.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function UploadCard(p: { hasCurrent: boolean; hasCnpj: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [inputKey, setInputKey] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const send = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("file", file!);
      fd.append("password", password);
      return api<CertificateView>("/fiscal/certificate", { method: "POST", body: fd });
    },
    onSuccess: (c) => {
      setErrors({});
      setFile(null);
      setPassword("");
      setInputKey((k) => k + 1);
      toast("success", `Certificado enviado. Válido até ${dateBR(c.validTo)}.`);
      void qc.invalidateQueries({ queryKey: ["fiscal-certificate", companyId] });
      void qc.invalidateQueries({ queryKey: ["notifications", companyId] });
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else setErrors({ file: errorMessage(e) });
      toast("danger", errorMessage(e));
    },
  });
  const label = p.hasCurrent ? "Substituir certificado" : "Enviar certificado";
  return (
    <section className="box card mb-3" aria-labelledby="cert-upload">
      <div className="card-body">
        <h2 id="cert-upload" className="h5">
          {label}
        </h2>
        <p className="text-muted small">
          Arquivo .pfx ou .p12 do certificado A1 (e-CNPJ) e a senha dele. O arquivo e a senha são guardados
          cifrados e nunca são exibidos.
          {p.hasCurrent && " O certificado atual fica no histórico."}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (file && password) send.mutate();
          }}
        >
          <fieldset disabled={!p.hasCnpj || send.isPending} className="row align-items-start">
            <Field label="Arquivo do certificado" required className="col-md-5" error={errors.file}>
              <input
                key={inputKey}
                type="file"
                accept=".pfx,.p12,application/x-pkcs12"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Field>
            <Field label="Senha do certificado" required className="col-md-4" error={errors.password}>
              <input
                type="password"
                value={password}
                maxLength={128}
                autoComplete="off"
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <div className="col-md-3 mb-3 pt-md-4">
              <button type="submit" className="btn btn-primary mt-md-2" disabled={!file || !password}>
                {label}
              </button>
            </div>
          </fieldset>
        </form>
      </div>
    </section>
  );
}

function HistoryCard({ history }: { history: CertificateView[] }): ReactElement | null {
  if (history.length === 0) return null;
  return (
    <section className="box card mb-3" aria-labelledby="cert-history">
      <div className="card-body">
        <h2 id="cert-history" className="h5">
          Histórico
        </h2>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Titular</th>
                <th scope="col">Validade</th>
                <th scope="col">Enviado em</th>
                <th scope="col">Substituído em</th>
                <th scope="col">Impressão digital (SHA-1)</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{h.subjectName}</td>
                  <td>
                    {dateBR(h.validFrom)} a {dateBR(h.validTo)}
                  </td>
                  <td>{dateTime(h.createdAt)}</td>
                  <td>{h.replacedAt ? dateTime(h.replacedAt) : "—"}</td>
                  <td className="text-break">
                    <code>{hexGroups(h.thumbprintSha1)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
